import { WorkflowRunKind } from '@/apis/logiblocksAPI';
import type { LBWorkflow } from '@/proto/logiblocks/logiblocks';

/**
 * Where a run's graph comes from: the published version a live run ran, or the
 * graph stored with a test run.
 */
export type RunGraphSource =
  | { kind: WorkflowRunKind.LIVE; versionNumber: number }
  | { kind: WorkflowRunKind.TEST; runId: string };

// The most run graph snapshots to keep decoded in memory at once. Published
// versions and test run graphs never change, so a cached entry is never stale;
// the cap only bounds memory across a long session that opens many runs.
export const MAX_CACHED_RUN_GRAPH_SNAPSHOTS = 20;

// Insertion order is the recency order: the oldest key is the first one out.
const cache = new Map<string, LBWorkflow>();

/** Returns the cache key for a run graph source, distinct for a version and a test run. */
export function getRunGraphSourceKey(
  workflowId: string,
  source: RunGraphSource
): string {
  switch (source.kind) {
    case WorkflowRunKind.LIVE:
      return `${workflowId}:version:${source.versionNumber}`;
    case WorkflowRunKind.TEST:
      return `${workflowId}:test:${source.runId}`;
  }
}

/** The cached snapshot for a source, marked most-recently-used, or undefined on a miss. */
export function getCachedRunGraphSnapshot(
  workflowId: string,
  source: RunGraphSource
): LBWorkflow | undefined {
  const key = getRunGraphSourceKey(workflowId, source);
  const snapshot = cache.get(key);
  if (snapshot !== undefined) {
    // Re-insert to move this key to the newest position, so it is evicted last.
    cache.delete(key);
    cache.set(key, snapshot);
  }
  return snapshot;
}

/** Store a source's snapshot, dropping the least-recently-used one past the cap. */
export function putCachedRunGraphSnapshot(
  workflowId: string,
  source: RunGraphSource,
  snapshot: LBWorkflow
): void {
  const key = getRunGraphSourceKey(workflowId, source);
  // Delete first so a re-store also moves the key to the newest position.
  cache.delete(key);
  cache.set(key, snapshot);
  while (cache.size > MAX_CACHED_RUN_GRAPH_SNAPSHOTS) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) {
      break;
    }
    cache.delete(oldest);
  }
}

/** Empty the cache. For test isolation, since the cache outlives any component. */
export function clearRunGraphSnapshotCache(): void {
  cache.clear();
}
